// ─────────────────────────────────────────────────────────────
// @ekkam/shared — Shared utilities
// Pure functions used by both frontend (apps/web) and backend
// (apps/api). No platform-specific imports allowed here.
// ─────────────────────────────────────────────────────────────

/**
 * Strips HTML tags from a string.
 * Used by the FE for content previews and by the BE for
 * building AI context from sanitised comment/discussion content.
 */
export const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, '')

const NAMED_ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&#39;': "'",
  '&nbsp;': ' ',
}

/**
 * Decodes the HTML entities sanitize-html emits when it escapes a text node.
 *
 * Content is sanitised on write, so a comment reading "R&D budget" is stored
 * as "R&amp;D budget". Anywhere that output is treated as plain text rather
 * than parsed as HTML, the entities have to come back out or they show up
 * literally on screen.
 *
 * `&amp;` is decoded last so `&amp;lt;` comes back as the literal "&lt;" the
 * user typed, rather than collapsing all the way down to "<".
 */
export const decodeEntities = (text: string): string =>
  text
    .replace(/&(?:lt|gt|quot|apos|#39|nbsp);/g, (m) => NAMED_ENTITIES[m] ?? m)
    .replace(/&amp;/g, '&')

/**
 * Strips HTML and truncates to a preview length.
 * Used in card displays (FE) and notification previews (BE).
 *
 * @param html   - Raw HTML string
 * @param maxLen - Maximum plain-text length (default 200)
 */
export const contentPreview = (html: string, maxLen = 200): string =>
  stripHtml(html).slice(0, maxLen)

/**
 * Normalises a raw tag string to a clean slug.
 * - Lowercased
 * - Non-alphanumeric/hyphen chars replaced with hyphens
 * - Consecutive hyphens collapsed
 * - Leading/trailing hyphens trimmed
 *
 * Canonical implementation — used by the FE tag input,
 * the BE AI tag generator, and the BE tag slug resolver.
 *
 * "React Hooks" → "react-hooks"
 * "C++" → "c--"  (plus signs become hyphens)
 */
export const normaliseTag = (raw: string): string =>
  raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
